import {
  DownloadSimpleIcon,
  EyeIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  PaperPlaneTiltIcon,
  PencilSimpleIcon,
  PlusIcon,
} from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Trainer, useGetTrainers } from '@/api/trainers'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Select } from '@/components/ui/select'
import { RowAction } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { popupFactory, toastFactory } from '@/factories'
import { useDesignVersionToggle, useFilters, useSearchDraft, useSearchParams } from '@/hooks'
import { getApiErrorMessage } from '@/utils/error'

import { BuildingFilter } from '../shared/building-filter.styled'
import { CreatedDateRangePicker } from '../shared/created-date-range-picker'
import { exportCsv } from '../shared/export-csv'
import { filterByDateRange, paginateItems } from '../shared/person-table.utils'
import { useAccountEmailActions } from '../shared/use-account-email-actions'
import { useDetailsDeepLink } from '../shared/use-details-deep-link'
import {
  getStaffTableColumns,
  getStatusFilterOptions,
  STAFF_TABLE_ID,
  staffTableFiltersSchema,
  staffTableInitialFilters,
} from './staff.constants'
import { TrainerStatusFilter } from './staff.status-filter'
import {
  filterTrainers,
  filterTrainersByBuildings,
  filterTrainersByStatus,
  getTrainerBuildingOptions,
  getTrainersCsvFields,
  sortTrainers,
} from './staff.utils'
import { TrainerDetailsModal } from './trainer-details-modal'
import { TrainerFormModal } from './trainer-form-modal'

export const StaffPage = () => {
  const { t } = useTranslation(['organisation'])
  const { handleSendPasswordReset, handleSendSetupLink } = useAccountEmailActions()

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'staff',
    screen: 'Staff',
    to: { path: '/dashboard/organisation/staff/legacy' },
    version: 'new',
  })

  const { data, error, isLoading } = useGetTrainers()

  const searchParams = useSearchParams({
    initialState: staffTableInitialFilters,
    schema: staffTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: staffTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const { buildingIds, fromDate, limit, search, skip, sortBy, sortOrder, status, toDate } =
    tableFilters.filters

  const [searchDraft, handleSearchChange] = useSearchDraft(search, (value) =>
    tableFilters.setFilter('search', value),
  )

  const statusOptions = useMemo(() => getStatusFilterOptions(t), [t])
  const buildingOptions = useMemo(() => getTrainerBuildingOptions(data ?? []), [data])

  const filteredTrainers = useMemo(
    () =>
      sortTrainers(
        filterTrainers(
          filterTrainersByBuildings(
            filterTrainersByStatus(
              filterByDateRange(data ?? [], fromDate, toDate, (trainer) => trainer.createdAt),
              status,
            ),
            buildingIds,
          ),
          search,
        ),
        sortBy,
        sortOrder,
      ),
    [data, fromDate, toDate, status, buildingIds, search, sortBy, sortOrder],
  )

  const pageTrainers = useMemo(
    () => paginateItems(filteredTrainers, skip, limit),
    [filteredTrainers, skip, limit],
  )

  const handleCreate = () => {
    popupFactory.open(TrainerFormModal, { mode: 'create' })
  }

  const [isExporting, setIsExporting] = useState(false)

  const handleExportCsv = async () => {
    const selectedBuildings = buildingOptions.filter((option) =>
      buildingIds?.includes(option.value),
    )

    setIsExporting(true)
    try {
      await exportCsv({
        buildingName: selectedBuildings.length === 1 ? selectedBuildings[0].label : undefined,
        data: filteredTrainers,
        fields: getTrainersCsvFields(t),
        page: 'staff',
      })
    } catch (exportError) {
      toastFactory.error(getApiErrorMessage(exportError))
    } finally {
      setIsExporting(false)
    }
  }

  const handleStatusChange = (value: TrainerStatusFilter) => {
    tableFilters.setFilter('status', value)
  }

  const handleBuildingsChange = (values: number[]) => {
    tableFilters.setFilter('buildingIds', values.length ? values : undefined)
  }

  const handleSort = (nextSortBy: string | null, nextSortOrder: 'asc' | 'desc' | null) => {
    tableFilters.setFilters({
      sortBy: (nextSortBy as typeof sortBy) ?? undefined,
      sortOrder: nextSortOrder ?? undefined,
    })
  }

  const openDetails = useDetailsDeepLink(TrainerDetailsModal, (trainerId: number) => ({
    trainerId,
  }))

  const columns = useMemo(() => getStaffTableColumns(t, openDetails), [t, openDetails])

  const rowActions: RowAction<Trainer>[] = useMemo(
    () => [
      {
        icon: EyeIcon,
        label: t('organisation:staff.list.rowActions.details'),
        onClick: (trainer) => openDetails(trainer.id),
        primary: true,
      },
      {
        icon: PencilSimpleIcon,
        label: t('organisation:staff.list.rowActions.edit'),
        onClick: (trainer) => popupFactory.open(TrainerFormModal, { mode: 'edit', trainer }),
      },
      {
        icon: PaperPlaneTiltIcon,
        label: t('organisation:shared.accountEmails.sendSetupLink'),
        onClick: (trainer) => handleSendSetupLink(trainer.user.email),
      },
      {
        icon: KeyIcon,
        label: t('organisation:shared.accountEmails.sendPasswordReset'),
        onClick: (trainer) => handleSendPasswordReset(trainer.user.email),
      },
    ],
    [t, handleSendSetupLink, handleSendPasswordReset, openDetails],
  )

  if (redirect) return redirect

  return (
    <Page>
      <PageHeader
        title={t('organisation:staff.list.title')}
        actions={
          <>
            <ToggleSwitch
              falsyLabel={t('organisation:staff.list.designToggle.old')}
              truthyLabel={t('organisation:staff.list.designToggle.new')}
              checked={true}
              onChange={handleUIToggle}
            />

            <Button size="small" use="primary" onClick={handleCreate}>
              {t('organisation:staff.list.createNew')}
              <Icon icon={PlusIcon} />
            </Button>
          </>
        }
      />

      <TableGroup
        columns={columns}
        data={pageTrainers}
        emptyNoFilters={{
          description: t('organisation:staff.list.empty.noFilters.description'),
          title: t('organisation:staff.list.empty.noFilters.title'),
        }}
        emptyWithFilters={{
          description: t('organisation:staff.list.empty.withFilters.description'),
          title: t('organisation:staff.list.empty.withFilters.title'),
        }}
        error={error}
        filters={
          <>
            <Select
              options={statusOptions}
              placeholder={t('organisation:staff.list.filters.status.placeholder')}
              size="small"
              value={status ?? TrainerStatusFilter.all}
              onChange={handleStatusChange}
              onReset={() => handleStatusChange(TrainerStatusFilter.all)}
            />
            <BuildingFilter>
              <Select
                searchable
                type="multi"
                options={buildingOptions}
                placeholder={t('organisation:staff.list.filters.buildings')}
                size="small"
                value={buildingIds ?? []}
                onChange={handleBuildingsChange}
                onReset={() => handleBuildingsChange([])}
              />
            </BuildingFilter>
          </>
        }
        getRowKey={(trainer) => trainer.id}
        id={STAFF_TABLE_ID}
        loading={isLoading}
        navigation={{
          actions: (
            <Button
              onlyBox
              disabled={!filteredTrainers.length}
              loading={isExporting}
              size="small"
              title={t('organisation:staff.list.exportCsv')}
              use="secondary"
              onClick={handleExportCsv}
            >
              <Icon icon={DownloadSimpleIcon} />
            </Button>
          ),
          datePicker: (
            <CreatedDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              onChange={(range) => tableFilters.setFilters(range)}
            />
          ),
          search: (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              name="search"
              placeholder={t('organisation:staff.list.search.placeholder')}
              size="small"
              value={searchDraft}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          ),
        }}
        pagination={{
          paginationFilters: tableFilters,
          total: filteredTrainers.length,
        }}
        rowActions={rowActions}
        sortBy={sortBy ?? null}
        sortOrder={sortOrder ?? null}
        tableFilters={tableFilters}
        onSort={handleSort}
      />
    </Page>
  )
}
